"use client"
import { Link } from "react-router-dom"
import Footer from "./Footer"

const Features = () => {
  const featuresList = [
    {
      icon: "🔍",
      title: "Product Extraction",
      description:
        "Extract all products from any Shopify store with a single click. Get titles, descriptions, images, prices, and more.",
    },
    {
      icon: "📊",
      title: "Collection Export",
      description:
        "Export products by collection to organize your data. Filter and select specific collections to export only what you need.",
    },
    {
      icon: "🔄",
      title: "Multiple Format Support",
      description:
        "Export your data in CSV format compatible with both Shopify and WooCommerce platforms for seamless integration.",
    },
    {
      icon: "⚡",
      title: "Lightning Fast",
      description: "Our optimized algorithms extract data at incredible speeds, saving you hours of manual work.",
    },
    {
      icon: "🔎",
      title: "Advanced Search",
      description: "Search for specific products within a store and export only the results you need.",
    },
    {
      icon: "📱",
      title: "Mobile Responsive",
      description: "Access your exported data on any device with our responsive web interface.",
    },
    {
      icon: "🔒",
      title: "Secure Data",
      description: "Your data is securely stored and accessible only to you. We prioritize your privacy and security.",
    },
    {
      icon: "📈",
      title: "Detailed Analytics",
      description: "Get insights into the products you've exported with detailed analytics and statistics.",
    },
  ]

  return (
    <div className="features-page">
      {/* Hero Section */}
      <section className="features-hero">
        <div className="features-hero-container">
          <h1 className="features-hero-title">Powerful Features to Boost Your Productivity</h1>
          <p className="features-hero-description">
            Discover all the tools and features that make Shopify Scraper the preferred choice for e-commerce
            professionals
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="features-grid-section">
        <div className="features-grid-container">
          <div className="features-grid">
            {featuresList.map((feature, index) => (
              <div className="feature-card" key={index}>
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Feature Highlight */}
      <section className="key-feature-section">
        <div className="key-feature-container">
          <div className="key-feature-content">
            <h2 className="key-feature-title">One-Click Export</h2>
            <p className="key-feature-description">
              With just a single click, you can export all products from any Shopify store. Our powerful tool
              automatically extracts product details, images, variants, and pricing information, saving you hours of
              manual work.
            </p>
            <ul className="key-feature-list">
              <li>Export up to 1000+ products in under a minute</li>
              <li>Get complete product information including variants</li>
              <li>Download high-quality product images</li>
              <li>Export directly to CSV format</li>
            </ul>
          </div>
          <div className="key-feature-image">
            <img
              src="https://via.placeholder.com/600x400/00a99d/FFFFFF?text=One-Click+Export+Feature"
              alt="One-Click Export Feature"
              className="key-feature-illustration"
            />
          </div>
        </div>
      </section>

      {/* Second Key Feature */}
      <section className="key-feature-section alternate">
        <div className="key-feature-container reverse">
          <div className="key-feature-image">
            <img
              src="https://via.placeholder.com/600x400/00a99d/FFFFFF?text=Collection+Export+Feature"
              alt="Collection Export Feature"
              className="key-feature-illustration"
            />
          </div>
          <div className="key-feature-content">
            <h2 className="key-feature-title">Collection Export</h2>
            <p className="key-feature-description">
              Target specific collections to export only the products you're interested in. Filter by collection to
              organize your data and streamline your workflow.
            </p>
            <ul className="key-feature-list">
              <li>View all collections from any Shopify store</li>
              <li>Select specific collections to export</li>
              <li>Filter products within collections</li>
              <li>Organize exported data by collection</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="features-cta-section">
        <div className="features-cta-container">
          <h2 className="features-cta-title">Ready to experience these features?</h2>
          <p className="features-cta-description">
            Join thousands of e-commerce professionals who are already saving time with Shopify Scraper
          </p>
          <div className="features-cta-buttons">
            <Link to="/home" className="features-cta-button primary">
              Try It Now
            </Link>
            <Link to="/pricing" className="features-cta-button secondary">
              View Pricing Plans
            </Link>
          </div>
        </div>
      </section>
      
      {/* Styling */}
      <style jsx="true">{`
        /* Features Hero Section */
        .features-hero {
          background-color: #007BFF;
          color: white;
          padding: 5rem 0;
          text-align: center;
        }
        
        .features-hero-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        
        .features-hero-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
        }
        
        .features-hero-description {
          font-size: 1.25rem;
          max-width: 800px;
          margin: 0 auto;
          opacity: 0.9;
        }
        
        /* Features Grid */
        .features-grid-section {
          padding: 5rem 0;
          background-color: #f9fafb;
        }
        
        .features-grid-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        
        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 2rem;
        }
        
        .feature-card {
          background-color: white;
          padding: 2rem;
          border-radius: 0.5rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        
        .feature-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
        }
        
        .feature-icon {
          font-size: 2.5rem;
          margin-bottom: 1.5rem;
        }
        
        .feature-title {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 1rem;
          color: #1a202c;
        }
        
        .feature-description {
          font-size: 1rem;
          color: #4a5568;
          line-height: 1.6;
        }
        
        /* Key Feature Section */
        .key-feature-section {
          padding: 5rem 0;
          background-color: white;
        }
        
        .key-feature-section.alternate {
          background-color: #f9fafb;
        }
        
        .key-feature-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
          display: flex;
          align-items: center;
          gap: 3rem;
        }
        
        .key-feature-container.reverse {
          flex-direction: row-reverse;
        }
        
        .key-feature-content {
          flex: 1;
        }
        
        .key-feature-title {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          color: #1a202c;
        }
        
        .key-feature-description {
          font-size: 1.125rem;
          color: #4a5568;
          line-height: 1.6;
          margin-bottom: 1.5rem;
        }
        
        .key-feature-list {
          padding-left: 1.5rem;
        }
        
        .key-feature-list li {
          margin-bottom: 0.75rem;
          color: #4a5568;
          line-height: 1.6;
        }
        
        .key-feature-image {
          flex: 1;
          display: flex;
          justify-content: center;
        }
        
        .key-feature-illustration {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
        }
        
        /* CTA Section */
        .features-cta-section {
          padding: 5rem 0;
          background-color: #007BFF;
          color: white;
          text-align: center;
        }
        
        .features-cta-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        
        .features-cta-title {
          font-size: 2.25rem;
          font-weight: 700;
          margin-bottom: 1rem;
        }
        
        .features-cta-description {
          font-size: 1.125rem;
          margin-bottom: 2rem;
          opacity: 0.9;
          max-width: 800px;
          margin-left: auto;
          margin-right: auto;
        }
        
        .features-cta-buttons {
          display: flex;
          justify-content: center;
          gap: 1rem;
          flex-wrap: wrap;
        }
        
        .features-cta-button {
          padding: 0.75rem 1.5rem;
          border-radius: 0.375rem;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.2s;
        }
        
        .features-cta-button.primary {
          background-color: white;
          color: #007BFF;
        }
        
        .features-cta-button.primary:hover {
          background-color: #f9fafb;
        }
        
        .features-cta-button.secondary {
          background-color: transparent;
          border: 2px solid white;
          color: white;
        }
        
        .features-cta-button.secondary:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
        
        /* Responsive Styles */
        @media (max-width: 1024px) {
          .key-feature-container {
            flex-direction: column;
          }
          
          .key-feature-container.reverse {
            flex-direction: column;
          }
        }
        
        @media (max-width: 768px) {
          .features-hero-title {
            font-size: 2rem;
          }
          
          .features-hero-description {
            font-size: 1.125rem;
          }
          
          .key-feature-title {
            font-size: 1.75rem;
          }
          
          .features-cta-title {
            font-size: 1.75rem;
          }
        }
      `}</style>
    </div>
  )
}

export default Features
